/**
 * tutorStudentsDeskViewModel — `(reduxState, studentId, expandedDone) → props` de « Mes élèves » dans le cadre
 * du live, pour l'élève regardé (patron A, sur la lecture de sa fiche, `getStudent`).
 *
 * Les mêmes règles que la salle du live, sur la même forme d'élève (`StudentResource`, rangé dans
 * `currentUser.students`) :
 *  - **sa liste d'activités** est celle de la pellicule (`tutorLiveActivitiesOf`) : ses faites selon la règle de
 *    l'ancienne liste (toutes une fois « Voir plus » touché), puis « À faire » dans l'ordre du serveur. Hors live,
 *    personne n'a d'activité devant lui ;
 *  - **sa tête au centre** (`UITutorLiveStudent`) dit ses matières comme le live sans séance
 *    (`toSessionSubjects`, sans objectif de séance : ses matières prioritaires) ; ni mode, ni présence, ni chrono
 *    n'y sont lus hors live ;
 *  - **sa mission du champion** (`sam_mission`), celle que son accueil lui montre (`toStudentSamMission`).
 *
 * Une fabrique : sa mémoïsation est celle de l'écran qui la monte (`ports/CLAUDE.md` § view-models).
 */
import moment from "moment"
import { createSelector } from "reselect"

import { indexSubjectsById, type ApiSchoolSubject } from "../../../adapters/parent/shared/schoolSubjectAdapter"
import { type ApiSamMission, toStudentSamMission } from "../../../adapters/student/Home/samMissionAdapter"
import { toSessionSubjects } from "../../../adapters/tutor/Live/sessionObjectiveAdapter"
import {
    type ApiActivityTemplate,
    type ApiLiveStudent,
    displayNameOf,
    tutorLiveActivitiesOf,
} from "../../../adapters/tutor/Live/tutorLiveStudentAdapter"
import { type UISamMission } from "../../../ui/types/studentHome"
import { type UITutorLiveActivity, type UITutorLiveStudent } from "../../../ui/types/tutorLive"
import { selectDisplayedStudent } from "../../shared/displayedStudent"

export type TutorStudentsDeskViewModel = {
    /** L'élève regardé, tel que le centre du live le lit ; `null` tant que sa fiche n'est pas lue. */
    student: UITutorLiveStudent | null
    activities: UITutorLiveActivity[]
    moreDone: boolean
    /** Sa mission du champion ; `null` sans mission (ou le champion fermé pour lui). */
    mission: (UISamMission & { firstname: string }) | null
}

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type ReduxState = any

type StoredStudent = ApiLiveStudent & {
    id?: number | string
    avatar?: string | null
    profile?: { class?: string | null } | null
    sam_mission?: ApiSamMission | null
}

const EMPTY: TutorStudentsDeskViewModel = { student: null, activities: [], moreDone: false, mission: null }

/** Hors live, le chrono de la vignette n'est lu nulle part : arrêté, à zéro. */
const OFF_LIVE_CHRONO: UITutorLiveStudent["chrono"] = { kind: "since", seconds: 0 }

export function makeTutorStudentsDeskViewModel() {
    return createSelector(
        [
            (state: ReduxState, studentId: string | number | null) =>
                studentId == null ? null : (selectDisplayedStudent(state, studentId) as StoredStudent | null),
            (state: ReduxState) => state?.data?.staticData?.school_subjects as ApiSchoolSubject[] | undefined,
            (state: ReduxState) =>
                state?.data?.staticData?.activity_templates as ApiActivityTemplate[] | null | undefined,
            (_state: ReduxState, _studentId: string | number | null, expandedDone: boolean) => expandedDone,
        ],
        (student, subjects, templates, expandedDone): TutorStudentsDeskViewModel => {
            if (student?.id == null) return EMPTY
            const subjectsById = indexSubjectsById(subjects)
            const { activities, moreDone } = tutorLiveActivitiesOf(student, {
                subjectsById,
                templates,
                seenActivityId: null,
                expanded: expandedDone,
                today: moment(),
            })
            const firstname = displayNameOf(student)
            const mission = toStudentSamMission(student.sam_mission)
            return {
                student: {
                    id: String(student.id),
                    firstname,
                    grade: student.profile?.class ?? undefined,
                    avatarUrl: student.avatar ?? undefined,
                    soundMode: "dialogue",
                    presence: "offline",
                    chrono: OFF_LIVE_CHRONO,
                    activities,
                    moreDone,
                    objective: { subjects: toSessionSubjects(student, null, subjectsById) },
                },
                activities,
                moreDone,
                mission: mission != null ? { done: mission.done, total: mission.total, firstname } : null,
            }
        }
    )
}
